import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, FilePen, Trash2, Edit, Save, FolderInput } from 'lucide-react';
import { toast } from 'sonner';
import ImportarDelDriveDialog from '../components/ImportarDelDriveDialog';

// Los campos de la DETECCIÓN (backend: services/quirofano_matcher). Solo los
// cambia administración; a los demás ni se les mandan al guardar.
const CAMPOS_DETECCION = ['keywords', 'cede_ante', 'orden_deteccion'];

/** ¿Es un borrador importado del Drive que todavía no se ha activado?
 * (backend: services/importar_drive.es_borrador). No se ofrece al generar
 * consentimientos hasta que lo activa un administrador. */
export function esBorradorImportado(t) {
  return !!(t && t.importado && t.active === false);
}

/** Las palabras escritas una por línea → lista limpia, sin repetidas. */
export function palabrasDeDeteccion(texto) {
  const vistas = new Set();
  return String(texto || '')
    .split('\n')
    .map((l) => l.trim().replace(/\s+/g, ' '))
    .filter((l) => l && !vistas.has(l) && vistas.add(l));
}

function TemplateDialog({ open, onOpenChange, initial, onSaved, esAdmin, otras }) {
  const isEdit = !!initial?.id;
  // Each section carries a stable local _uid used only for React keys; stripped before POST.
  const newSection = (title = '', body = '') => ({ _uid: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, title, body });
  const withUids = (sections) => (sections || []).map(s => s._uid ? s : { ...newSection(s.title, s.body) });
  const [form, setForm] = useState(() => {
    const base = initial || { code: '', name: '', surgery_label: '', intro: '', sections: [{ title: '', body: '' }], consent_text: '', authorizations: [], active: true };
    return { ...base, sections: withUids(base.sections) };
  });
  const [authStr, setAuthStr] = useState((initial?.authorizations || []).join('\n'));
  const [kwStr, setKwStr] = useState((initial?.keywords || []).join('\n'));
  const [cede, setCede] = useState(initial?.cede_ante || []);
  const [orden, setOrden] = useState(initial?.orden_deteccion ?? '');
  useEffect(() => {
    if (open) {
      const i = initial || { code: '', name: '', surgery_label: '', intro: '', sections: [{ title: '', body: '' }], consent_text: '', authorizations: [], active: true };
      setForm({ ...i, sections: withUids(i.sections) });
      setAuthStr((i.authorizations || []).join('\n'));
      setKwStr((i.keywords || []).join('\n'));
      setCede(i.cede_ante || []);
      setOrden(i.orden_deteccion ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSection = (i, k, v) => setForm(f => ({ ...f, sections: f.sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addSection = () => setForm(f => ({ ...f, sections: [...f.sections, newSection()] }));
  const removeSection = (i) => setForm(f => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));
  const alternarCede = (code) => setCede(c => (c.includes(code) ? c.filter(x => x !== code) : [...c, code]));
  const submit = async (e) => {
    e.preventDefault();
    // Strip internal _uid from sections before persisting.
    const cleanSections = (form.sections || []).map(({ _uid, ...rest }) => rest);
    const payload = { ...form, sections: cleanSections, authorizations: authStr.split('\n').filter(Boolean) };
    CAMPOS_DETECCION.forEach((k) => { delete payload[k]; });
    if (esAdmin) {
      payload.keywords = palabrasDeDeteccion(kwStr);
      payload.cede_ante = cede.filter((c) => c && c !== form.code);
      payload.orden_deteccion = String(orden).trim() === '' ? null : Number(orden);
    }
    try {
      if (isEdit) { await api.put(`/api/consent-templates/${initial.id}`, payload); toast.success('Actualizada'); }
      else { await api.post('/api/consent-templates', payload); toast.success('Creada'); }
      onSaved && onSaved(); onOpenChange(false);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const candidatas = (otras || []).filter((t) => t.code && t.code !== form.code);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Código</Label><Input required value={form.code} disabled={isEdit} onChange={(e) => setF('code', e.target.value)} /></div>
            <div><Label>Nombre</Label><Input required value={form.name} onChange={(e) => setF('name', e.target.value)} /></div>
            <div><Label>Etiqueta (badge)</Label><Input required value={form.surgery_label} onChange={(e) => setF('surgery_label', e.target.value)} /></div>
          </div>
          {form.importado && (
            <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs space-y-1" data-testid="template-importado">
              <div className="font-semibold text-[#13231E]">Importado de: {form.importado.ruta || form.importado.archivo}</div>
              <div>Es un documento legal: el médico lo revisa (el texto, las secciones y los marcadores {'{{DOCTOR}}'} y {'{{PACIENTE}}'}) y un administrador lo activa. Mientras esté sin activar no se ofrece al generar consentimientos.</div>
              {(form.importado.avisos || []).length > 0 && (
                <ul className="list-disc pl-5 text-amber-900">{form.importado.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
              )}
              <label className="flex items-center gap-2 text-sm pt-1">
                <input type="checkbox" checked={form.active !== false} disabled={!esAdmin}
                  onChange={(e) => setF('active', e.target.checked)} data-testid="template-activa" />
                Activa (revisada por el médico){!esAdmin && ' — la activa un administrador'}
              </label>
            </div>
          )}
          {/* Activa o no (20/10/2026): desactivada no se ofrece al generar
              consentimientos y lo ya hecho con ella sigue igual. Es lo que se
              hace en vez de borrar una plantilla con consentimientos. La de un
              borrador importado va en su aviso, arriba. Solo administración. */}
          {esAdmin && isEdit && !form.importado && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setF('active', e.target.checked)}
                data-testid="template-activa-normal" />
              Activa (se ofrece al generar consentimientos; desactivada no, y lo ya hecho con ella sigue igual)
            </label>
          )}
          {/* La marca de anestesia decide quién firma el cuadro del médico: solo
              la ve y la cambia administración (el servidor lo exige igual). */}
          {esAdmin && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.anesthesia} onChange={(e) => setF('anesthesia', e.target.checked)}
                data-testid="template-anestesia" />
              Consentimiento de anestesia (el cuadro del médico lo firma el anestesista)
              {form.anestesia_propuesta && !form.anesthesia && (
                <span className="text-xs text-amber-800" data-testid="template-anestesia-propuesta">· el importador lo propone: habla de anestesia</span>
              )}
            </label>
          )}
          <div><Label>Introducción (DECLARO)</Label><Textarea rows={3} value={form.intro} onChange={(e) => setF('intro', e.target.value)} /></div>
          <div>
            <div className="flex items-center justify-between mb-1"><Label>Secciones</Label><Button type="button" variant="outline" size="sm" onClick={addSection}><Plus className="w-3.5 h-3.5 mr-1" /> Añadir sección</Button></div>
            {form.sections.map((s, i) => (
              <div key={s._uid} className="border border-border rounded-lg p-3 mb-2 space-y-2">
                <div className="flex items-center gap-2"><Input placeholder="Título" value={s.title} onChange={(e) => setSection(i, 'title', e.target.value)} /><Button type="button" variant="ghost" size="icon" onClick={() => removeSection(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></div>
                <Textarea rows={3} placeholder="Cuerpo de la sección…" value={s.body} onChange={(e) => setSection(i, 'body', e.target.value)} />
              </div>
            ))}
          </div>
          <div><Label>Texto del consentimiento (sigue a "CONSIENTO")</Label><Textarea rows={2} value={form.consent_text} onChange={(e) => setF('consent_text', e.target.value)} /></div>
          <div><Label>Autorizaciones (una por línea)</Label><Textarea rows={2} value={authStr} onChange={(e) => setAuthStr(e.target.value)} /></div>
          <fieldset className="border border-border rounded-lg p-3 space-y-3" data-testid="template-deteccion" disabled={!esAdmin}>
            <legend className="px-1 text-sm font-semibold text-[#13231E]">Detección en la cita de quirófano</legend>
            <p className="text-xs text-muted-foreground">
              Cuando el procedimiento o la nota de una cita dicen alguna de estas palabras, esta plantilla se
              preselecciona. Se comparan sin tildes ni mayúsculas y como palabras enteras; una que termina
              en <span className="font-mono">*</span> vale para el principio de una palabra
              (<span className="font-mono">endo*</span> → «endoscópico»). Pon también las abreviaturas del equipo.
              {!esAdmin && <strong className="block mt-1">Solo administración puede cambiar la detección.</strong>}
            </p>
            <div>
              <Label htmlFor="template-keywords">Palabras que la detectan (una por línea)</Label>
              <Textarea id="template-keywords" rows={4} value={kwStr} onChange={(e) => setKwStr(e.target.value)} data-testid="template-keywords" />
            </div>
            {candidatas.length > 0 && (
              <div>
                <Label>Se retira si en el mismo texto sale también…</Label>
                <p className="text-xs text-muted-foreground mb-1">Para una plantilla genérica que sobra cuando el texto nombra la técnica concreta.</p>
                <div className="flex flex-wrap gap-1.5">
                  {candidatas.map((t) => {
                    const marcada = cede.includes(t.code);
                    return (
                      <button key={t.code} type="button" onClick={() => alternarCede(t.code)} aria-pressed={marcada}
                        data-testid={`template-cede-${t.code}`}
                        className={`px-2 py-0.5 rounded-full border text-xs ${marcada ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : 'border-border text-[#13231E]'}`}>
                        {t.name || t.code}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="max-w-[12rem]">
              <Label htmlFor="template-orden">Orden de detección</Label>
              <Input id="template-orden" type="number" min={0} step={1} placeholder="al final" value={orden}
                onChange={(e) => setOrden(e.target.value)} data-testid="template-orden" />
            </div>
          </fieldset>
          <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="template-guardar"><Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsentTemplatesPage() {
  const { user } = useAuth();
  const esAdmin = (user?.role || '') === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importarOpen, setImportarOpen] = useState(false);
  const fetchItems = async () => { setLoading(true); try { const r = await api.get('/api/consent-templates'); setItems(r.data); } finally { setLoading(false); } };
  useEffect(() => { fetchItems(); }, []);
  // Con consentimientos hechos con ella no se borra (el servidor dice 409 y
  // por qué: se desactiva en su editor).
  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    try { await api.delete(`/api/consent-templates/${id}`); toast.success('Eliminada'); fetchItems(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'No se ha podido borrar'); }
  };
  return (
    <div>
      <PageHeader title="Plantillas de consentimientos" subtitle={`${items.length} plantillas (sin sección de revocación)`}
        actions={<div className="flex gap-2">
          {/* Importar desde el Drive (20/10/2026): solo administración. */}
          {esAdmin && <Button variant="outline" onClick={() => setImportarOpen(true)} data-testid="template-importar-drive"><FolderInput className="w-4 h-4 mr-2" /> Importar desde el Drive</Button>}
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="template-new"><Plus className="w-4 h-4 mr-2" /> Nueva plantilla</Button>
        </div>} />
      <div className="cjo-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : items.length === 0 ? <EmptyState icon={FilePen} title="Sin plantillas" description="Añade la primera." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Código</th><th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th><th className="py-3 px-4 font-semibold text-[#13231E]">Badge</th><th className="py-3 px-4 font-semibold text-[#13231E]">Secciones</th><th className="py-3 px-4 font-semibold text-[#13231E]">Detección</th><th></th></tr></thead>
            <tbody>{items.map(t => <tr key={t.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="template-row">
              <td className="py-3 px-4 text-xs font-mono">{t.code}</td>
              <td className="py-3 px-4 font-medium">
                {t.name}
                {!t.importado && t.active === false && <span className="ml-2 text-[10px] uppercase tracking-wider bg-muted text-muted-foreground border border-border px-1.5 py-0.5 rounded" data-testid={`template-inactiva-${t.code}`}>Desactivada</span>}
                {esBorradorImportado(t) && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5 rounded" data-testid={`template-borrador-${t.code}`}>Borrador · sin activar</span>}
                {t.importado && <div className="text-[11px] text-muted-foreground font-normal" data-testid={`template-ruta-${t.code}`}>importado de: {t.importado.ruta || t.importado.archivo}</div>}
              </td>
              <td className="py-3 px-4 text-xs">{t.surgery_label}</td>
              <td className="py-3 px-4 text-xs">{(t.sections || []).length} secciones</td>
              <td className="py-3 px-4 text-xs" data-testid={`template-deteccion-${t.code}`}>{(t.keywords || []).length ? `${t.keywords.length} palabras` : '—'}</td>
              <td className="py-3 px-4 text-right"><div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid="template-edit"><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div></td>
            </tr>)}</tbody>
          </table></div>}
      </div>
      <TemplateDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} esAdmin={esAdmin} otras={items} />
      <ImportarDelDriveDialog open={importarOpen} onOpenChange={setImportarOpen} tipo="consentimiento" onImportado={fetchItems} />
    </div>
  );
}
